import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { site, pages } from '../data'

export const LOCALES = ['en', 'ar']

/** Locale of a URL path: everything under /ar is Arabic. */
export function localeOfPath (path) {
  return path === '/ar' || path.startsWith('/ar/') ? 'ar' : 'en'
}

/** The path without its locale prefix, always starting with "/". */
export function stripLocale (path) {
  if (path === '/ar') {
    return '/'
  }
  return path.startsWith('/ar/') ? path.slice(3) : path
}

/** Adds the locale prefix for a locale-less app path such as "/products". */
export function withLocale (path, locale) {
  if (locale !== 'ar') {
    return path
  }
  return path === '/' ? '/ar' : `/ar${path}`
}

function lookup (tree, path) {
  return path.split('.').reduce((node, key) => (node == null ? undefined : node[key]), tree)
}

const isFilled = (value) => (Array.isArray(value) ? value.length > 0 : value !== undefined && value !== null && value !== '')

/**
 * Per-component access to the current language and its copy.
 *
 *   t('hero.h1a')       text, falling back to English, then to ''
 *   list('faq.items')   array, falling back to English, then to []
 *   pick(item, 'name')  a CMS record's field, preferring its Arabic twin (nameAr)
 *   to('/products')     a locale-aware link target
 */
export function useLocale () {
  const route = useRoute()

  const locale = computed(() => localeOfPath(route.path))
  const isAr = computed(() => locale.value === 'ar')
  const dir = computed(() => (isAr.value ? 'rtl' : 'ltr'))
  const arrow = computed(() => (isAr.value ? '←' : '→'))

  function t (path) {
    const own = lookup(site[locale.value], path)
    const value = isFilled(own) ? own : lookup(site.en, path)
    return typeof value === 'string' ? value : ''
  }

  function list (path) {
    const own = lookup(site[locale.value], path)
    const value = Array.isArray(own) && own.length ? own : lookup(site.en, path)
    return Array.isArray(value) ? value : []
  }

  const ui = (key) => t(`ui.${key}`)

  function pick (item, key) {
    if (!item) {
      return undefined
    }
    if (isAr.value) {
      const arabic = item[`${key}Ar`]
      if (isFilled(arabic)) {
        return arabic
      }
    }
    return item[key]
  }

  const to = (path) => withLocale(path, locale.value)

  /** The same page in the other language, keeping query string and hash. */
  const switchTarget = computed(() => {
    const other = isAr.value ? 'en' : 'ar'
    return {
      path: withLocale(stripLocale(route.path), other),
      query: route.query,
      hash: route.hash
    }
  })

  const contactEmail = pages.contact?.email || 'hello@maaloufandpartners.com'

  return { locale, isAr, dir, arrow, t, list, ui, pick, to, switchTarget, contactEmail }
}
